import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  DollarSign,
  Clock,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HospitalQuoteApprovalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuoteDetails();
  }, [token]);

  useEffect(() => {
    // Auto-process if action is in URL
    if (action && quoteDetails && quoteDetails.status === "pending") {
      handleAction(action);
    }
  }, [action, quoteDetails]);

  const fetchQuoteDetails = async () => {
    try {
      const res = await fetch(`${API}/hospital/approve-quote/${token}`);
      if (res.ok) {
        const data = await res.json();
        setQuoteDetails(data);
        if (data.status !== "pending") {
          setResult(data.status);
        }
      } else {
        const err = await res.json();
        setError(err.detail || "Invalid approval link");
      }
    } catch (err) {
      setError("Failed to load quote details");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionType) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/hospital/approve-quote/${token}?action=${actionType}`, {
        method: "POST"
      });
      
      if (res.ok) {
        const data = await res.json();
        setResult(data.action);
        toast.success(`Quote ${data.action} successfully`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to process");
      }
    } catch (err) {
      toast.error("Failed to process request");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {result === "approved" ? (
              <>
                <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                <h2 className="text-xl font-bold text-slate-900 mb-2">Quote Approved!</h2>
                <p className="text-slate-600">
                  Thank you for approving the quote. Work will proceed as scheduled.
                </p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-slate-900 mb-2">Quote Declined</h2>
                <p className="text-slate-600">
                  The quote has been declined. A representative will contact you to discuss alternatives.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-emerald-600 mb-2">Ask Isaac</h1>
          <p className="text-slate-600">Quote Approval</p>
        </div>

        {/* Quote Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Quote from AskIsaac Inc.
            </CardTitle>
            <CardDescription>
              Please review and approve or decline this quote
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-slate-500">Reference</p>
              <p className="font-medium font-mono">{quoteDetails?.work_order_id || quoteDetails?.quote_ref}</p>
            </div>
            
            {quoteDetails?.scope && (
              <div>
                <p className="text-sm text-slate-500">Scope of Work</p>
                <p className="font-medium">{quoteDetails.scope}</p>
              </div>
            )}
            
            {quoteDetails?.item_name && (
              <div>
                <p className="text-sm text-slate-500">Item</p>
                <p className="font-medium">{quoteDetails.item_name}</p>
                {quoteDetails?.quantity && (
                  <p className="text-sm text-slate-600">Quantity: {quoteDetails.quantity} {quoteDetails.quantity_type || 'units'}</p>
                )}
              </div>
            )}
            
            {/* Sourced by Sterile Mate - No supplier visible */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-sm text-slate-500">Sourced By</p>
              <p className="font-medium text-slate-800">AskIsaac Inc.</p>
              <p className="text-xs text-slate-400">Your trusted healthcare supply partner</p>
            </div>

            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-emerald-800">
                  <DollarSign className="w-5 h-5" />
                  Total Amount
                </span>
                <span className="text-2xl font-bold text-emerald-600">
                  ${quoteDetails?.customer_price?.toFixed(2) || quoteDetails?.hospital_total?.toFixed(2)}
                </span>
              </div>
              {quoteDetails?.lead_time_days && (
                <div className="flex items-center gap-2 mt-2 text-emerald-700 text-sm">
                  <Clock className="w-4 h-4" />
                  Estimated delivery: {quoteDetails.lead_time_days} business days
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={() => handleAction("approve")}
                disabled={processing}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                size="lg"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Approve
                  </>
                )}
              </Button>
              <Button 
                onClick={() => handleAction("decline")}
                disabled={processing}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                size="lg"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-5 h-5 mr-2" />
                    Decline
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500">
          © 2026 AskIsaac Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
